<template>
  <div
    ref="reelContainerRef"
    class="reel-section relative w-full overflow-hidden"
  >
    <!-- Top Header: Dark section with text (separate from video) -->
    <div class="reel-header">
      <div class="reel-header__inner flex flex-col items-center">
        <h2 class="reel-heading text-center">
          <WrGradientText
            text="Building Intelligent Experiences with AI"
            append-class="pb-0 lg:pb-4"
          />
        </h2>

        <p class="text-lg tracking-[0.5px] font-anvtgrade-book">
            Creating smarter digital solutions powered by AI,
            <br class="hidden lg:block" />
            innovation, and human creativity.
          </p>
      </div>
    </div>

    <!-- Video Container -->
    <div class="reel-video-wrapper relative px-4 md:px-8 lg:px-[234px]">
      <div class="reel-video-inner relative">
        <!-- Background Loop Video -->
        <video
          ref="videoRef"
          autoplay
          loop
          muted
          playsinline
          preload="auto"
          class="reel-loop-video"
        >
          <source src="/images/WRI_HOMEPAGE.mp4" type="video/mp4" />
        </video>

        <!-- Purple Overlay -->
        <div class="reel-purple-overlay absolute inset-0" />

        <!-- Video Overlay Content -->
        <div
          class="reel-video-content absolute inset-0 z-10"
          @click="openLightbox"
          @mouseenter="isHoveringVideo = true"
          @mouseleave="isHoveringVideo = false"
        >
          <!-- Play Button -->
          <button
            class="reel-play-btn"
            aria-label="Play video"
          >
            <svg
              class="reel-play-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="20"
              height="20"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
            <span class="reel-play-text">Play</span>
          </button>

          <!-- 2 Min Watch Label -->
          <div class="reel-watch-label flex items-center gap-2">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="16"
              height="16"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span class="reel-watch-text">2 Min Watch</span>
          </div>

          <!-- Custom Cursor Elements (desktop only) — teleported to body -->
          <Teleport v-if="isDesktop" to="body">
            <div
              id="reel-cursor-circle"
              class="reel-cursor-circle"
              :class="{ 'cursor-visible': cursorVisible, 'cursor-hover': isHoveringVideo }"
              :style="{ left: cursorX + 'px', top: cursorY + 'px' }"
            />
            <div
              id="reel-cursor-crosshair"
              class="reel-cursor-crosshair"
              :class="{ 'cursor-visible': cursorVisible }"
              :style="{ left: cursorX + 'px', top: cursorY + 'px' }"
            >+</div>
          </Teleport>
        </div>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <div
      v-show="lightboxOpen"
      class="reel-lightbox"
      :class="{ 'reel-lightbox--active': lightboxOpen }"
      @click.self="closeLightbox"
    >
      <!-- Dark Backdrop -->
      <div
        class="reel-lightbox__dark"
        :class="{ 'reel-lightbox__dark--visible': lightboxOpen }"
        @click="closeLightbox"
      />

      <!-- Video Container -->
      <div
        class="reel-lightbox__calc"
        :class="{ 'reel-lightbox__calc--visible': lightboxOpen }"
      >
        <!-- Close Button -->
        <button
          @click="closeLightbox"
          aria-label="Close video"
          class="reel-lightbox__close-btn"
        >
          <WrSVGIcon
            name="WrCrossIcon"
            class="text-white size-5"
          />
        </button>

        <!-- Lightbox Video -->
        <video
          ref="lightboxVideoRef"
          controlsList="nodownload nofullscreen noplaybackrate"
          playsinline
          preload="auto"
          class="reel-lightbox-video"
        ></video>

        <!-- Loading Spinner -->
        <div
          v-show="showSpinner"
          class="reel-lightbox__spinner"
        >
          <svg
            class="reel-spinner-icon"
            viewBox="0 0 50 50"
            width="50"
            height="50"
          >
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="rgba(255,255,255,0.3)"
              stroke-width="4"
            />
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="white"
              stroke-width="4"
              stroke-dasharray="126"
              stroke-dashoffset="126"
              class="reel-spinner-circle"
            />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';

const { isDesktop } = useDevice();

// DOM refs
const reelContainerRef = ref<HTMLElement | null>(null);
const lightboxVideoRef = ref<HTMLVideoElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);

// State
const lightboxOpen = ref(false);
const showSpinner = ref(false);
const isHoveringVideo = ref(false);

watch(lightboxOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : '';
});

const cursorX = ref(0);
const cursorY = ref(0);
const cursorVisible = ref(false);

// Cursor move handler — 
const handleCursorMove = (e: MouseEvent) => {
  if (lightboxOpen.value) return;
  cursorX.value = e.clientX;
  cursorY.value = e.clientY;
};

// Lightbox open
const openLightbox = async () => {
  lightboxOpen.value = true;
  showSpinner.value = true;

  await nextTick();

  // Set video source and play
  const video = lightboxVideoRef.value;
  if (video) {
    video.src = 'https://pub-e6113e12990e467f911a73ac65295946.r2.dev/Office-Video-Final.mp4';

    // Hide spinner when video is ready to play
    const onCanPlay = () => {
      showSpinner.value = false;
      video.play().catch((err) => {
        console.error('Video playback failed:', err);
      });
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('error', onError);
    };

    const onError = () => {
      showSpinner.value = false;
      console.error('Video failed to load');
    };

    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('error', onError);

    // Trigger load
    video.load();

    // Fallback: hide spinner after 5s and try to play anyway
    setTimeout(() => {
      showSpinner.value = false;
      video.play().catch(() => {});
    }, 5000);
  }
};

// Lightbox close
const closeLightbox = () => {
  lightboxOpen.value = false;

  const video = lightboxVideoRef.value;
  if (video) {
    video.pause();
    video.currentTime = 0;
    video.src = '';
    video.load(); // Clear the source completely
  }
};

// Escape key handler
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && lightboxOpen.value) {
    closeLightbox();
  }
};

onMounted(async () => {
  if (!isDesktop) return;

  const container = reelContainerRef.value;
  if (!container) return;

  // Wait for Teleport to render refs
  await nextTick();

  // Listen for mouse movement within the reel section
  container.addEventListener('mousemove', handleCursorMove, { passive: true });

  // Show cursor only when entering the video area, hide when leaving
  const videoOverlay = container.querySelector<HTMLElement>('.reel-video-content');
  if (videoOverlay) {
    videoOverlay.addEventListener('mouseenter', () => {
      cursorVisible.value = true;
    });

    videoOverlay.addEventListener('mouseleave', () => {
      cursorVisible.value = false;
    });
  }

  // Escape key for lightbox
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  const container = reelContainerRef.value;
  if (container) {
    container.removeEventListener('mousemove', handleCursorMove);
  }
  window.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});
</script>

<style scoped>
/* ========================================
   Reel Section
   ======================================== */

.reel-section {
  position: relative;
  width: 100%;
  overflow: hidden;
}

/* Restore cursor when lightbox is open */
.reel-section:has(.reel-lightbox--active) {
  cursor: default;
}

/* ========================================
   Header (separate dark area above video)
   ======================================== */

.reel-header {
  background: var(--color-1);
  padding: 2rem 1.25rem;
}

.my-app-dark .reel-header {
  background: var(--color-1);
}

.reel-header__inner {
  max-width: 56rem;
  margin: 0 auto;
  text-align: center;
}

@media (min-width: 768px) {
  .reel-header {
    padding: 4rem 4rem;
  }
}

.reel-heading {
  color: white;
  font-size: 26px;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 0;
}

@media (min-width: 768px) {
  .reel-heading {
    font-size: 48px;
  }
}

.reel-subtitle {
  font-size: 18px;
  line-height: 140%;
  color: #ffffff;
  font-family: 'AvantGarde-Book', sans-serif;
  font-weight: 400;
  letter-spacing: 2%;
  margin-top: 0.5rem;
  max-width: 42rem;
}

@media (min-width: 768px) {
  .reel-subtitle {
    font-size: 18px;
    line-height: 100%;
    margin-top: 1rem;
  }
}

/* ========================================
   Video Container
   ======================================== */

.reel-video-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.reel-video-inner {
  position: relative;
  width: 100%;
  padding-bottom: 75%; /* 4:3 on mobile */
  overflow: hidden;
}

@media (min-width: 768px) {
  .reel-video-inner {
    padding-bottom: 56.25%; /* 16:9 on desktop */
  }
}

.reel-loop-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
}

.reel-video-content {
  position: absolute;
  inset: 0;
  z-index: 10;
  cursor: pointer; /* whole overlay is now the click target */
}

/* ========================================
   Purple Overlay
   ======================================== */

.reel-purple-overlay {
  background: linear-gradient(
    180deg,
    rgba(80, 20, 140, 0.35) 0%,
    rgba(60, 15, 100, 0.4) 50%,
    rgba(30, 10, 60, 0.5) 100%
  );
}

/* ========================================
   Play Button (centered on video)
   ======================================== */

.reel-play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.7);
  color: #1a0a2e;
  font-size: 0.8125rem;
  font-weight: 600;
  pointer-events: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
}

@media (min-width: 768px) {
  .reel-play-btn {
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
  }
}

.reel-video-content:hover .reel-play-btn {
  transform: translate(-50%, -50%) scale(1.05);
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
}

.reel-play-icon {
  flex-shrink: 0;
}

.reel-play-text {
  font-weight: 600;
}

/* ========================================
   2 Min Watch Label (right side of video)
   ======================================== */

.reel-watch-label {
  position: absolute;
  top: 16px;
  right: 16px;
  color: white;
  pointer-events: none;
}

.reel-watch-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

@media (min-width: 768px) {
  .reel-watch-label {
    top: 16px;
    right: 16px;
  }

  .reel-watch-text {
    font-size: 0.875rem;
  }
}

/* ========================================
   Lightbox
   ======================================== */

.reel-lightbox {
  position: fixed;
  inset: 0;
  z-index: 1000000;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Backdrop */
.reel-lightbox__dark {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s linear, visibility 0.3s linear;
}

.reel-lightbox__dark--visible {
  opacity: 1;
  visibility: visible;
}

/* Video Container */
.reel-lightbox__calc {
  position: relative;
  width: 90vw;
  max-width: 1200px;
  aspect-ratio: 16 / 9;
  opacity: 0;
  visibility: hidden;
  transform: scale(0.9) rotate(0.001deg);
  transition:
    transform 0.3s cubic-bezier(0.625, 0.05, 0, 1),
    opacity 0.3s linear,
    visibility 0.3s linear;
}

.reel-lightbox__calc--visible {
  opacity: 1;
  visibility: visible;
  transform: scale(1) rotate(0.001deg);
}

/* Lightbox Video */
.reel-lightbox-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

/* ========================================
   Close Button
   ======================================== */

.reel-lightbox__close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 20;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.reel-lightbox__close-btn:hover {
  opacity: 0.7;
}

/* ========================================
   Loading Spinner
   ======================================== */

.reel-lightbox__spinner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.reel-spinner-icon {
  animation: reel-spinner-rotate 0.75s linear infinite;
}

.reel-spinner-circle {
  animation: reel-spinner-dash 1.5s ease-in-out infinite;
}

@keyframes reel-spinner-rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes reel-spinner-dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -126;
  }
}
</style>

<!-- Global styles for teleported cursor elements -->
<style>
/* Cursor Circle */
.reel-cursor-circle {
  position: fixed;
  width: 2.5em;
  height: 2.5em;
  border: 1px solid #fff;
  border-radius: 100em;
  pointer-events: none;
  mix-blend-mode: difference;
  z-index: 9999;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition:
    left 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    top 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    width 0.375s cubic-bezier(0.625, 0.05, 0, 1),
    height 0.375s cubic-bezier(0.625, 0.05, 0, 1),
    opacity 0.3s ease;
}

.reel-cursor-circle.cursor-visible {
  opacity: 1;
}

.reel-cursor-circle.cursor-hover {
  width: 3em;
  height: 3em;
}

/* Cursor Crosshair */
.reel-cursor-crosshair {
  position: fixed;
  font-size: 14px;
  color: #fff;
  pointer-events: none;
  mix-blend-mode: difference;
  z-index: 9999;
  transform: translate(-50%, -50%);
  line-height: 1;
  user-select: none;
  opacity: 0;
  transition:
    left 0.1s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    top 0.1s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.3s ease;
}

.reel-cursor-crosshair.cursor-visible {
  opacity: 1;
}
</style>